import { SUPPORTED_LANGS, pickContentLang, templateRegistry, type Lang } from "@majourneys/shared";

export type Locale = Lang;

const LOCALES: readonly Locale[] = SUPPORTED_LANGS;
export type BlockTone = "positive" | "negative" | "neutral";

export interface ItineraryStop {
  title?: Partial<Record<Locale, string>>;
  description?: Partial<Record<Locale, string>>;
  durationMinutes?: number | null;
  tags?: string[];
}

export interface ListSection {
  key: string;
  title: string;
  icon: string;
  tone: BlockTone;
  items: string[];
}

// One implementation, in @majourneys/shared. Two of these is how the app ended
// up three locales behind the site.
export const pickLocale = pickContentLang;

export function readList(
  blocks: Record<string, unknown> | null | undefined,
  key: string,
  locale: Locale,
): string[] {
  if (!blocks) return [];
  const block = blocks[key] as Partial<Record<Locale, unknown>> | undefined;
  if (!block) return [];
  const raw = block[locale] ?? block.en;
  if (!Array.isArray(raw)) return [];
  return raw.filter((it): it is string => typeof it === "string" && it.trim().length > 0);
}

export function readItinerary(
  blocks: Record<string, unknown> | null | undefined,
  key: string,
): ItineraryStop[] {
  if (!blocks) return [];
  const raw = blocks[key];
  if (!Array.isArray(raw)) return [];
  return raw as ItineraryStop[];
}

export function pickLocalized(
  value: Partial<Record<Locale, string>> | undefined,
  locale: Locale,
): string {
  if (!value) return "";
  return value[locale]?.trim() || value.en?.trim() || "";
}

export function formatDuration(min: number | null | undefined): string | null {
  if (!min || min <= 0) return null;
  if (min >= 1440) return null;
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export function pickupIsExcluded(excludedItems: string[]): boolean {
  const re = /\b(pick[-\s]?up|transfer|transport(ation)?)\b/i;
  return excludedItems.some((it) => re.test(it));
}

const TITLE_KEY_LABELS: Record<string, string> = {
  "blocks.highlights": "Highlights",
  "blocks.pickup_info": "Pickup info",
  "blocks.included": "Included",
  "blocks.excluded": "Not included",
  "blocks.not_suitable": "Not suitable for",
  "blocks.not_allowed": "Not allowed",
  "blocks.know_before_you_go": "Know before you go",
  "blocks.what_to_bring": "What to bring",
  "blocks.info_before_booking": "Info before booking",
  "blocks.property_details": "Property details",
  "blocks.best_for": "Best for",
  "blocks.amenities": "Amenities",
  "blocks.room_features": "Room features",
  "blocks.services": "Services",
  "blocks.safety": "Safety",
  "blocks.accessibility": "Accessibility",
  "blocks.check_in_out": "Check-in / check-out",
  "blocks.house_rules": "House rules",
  "blocks.menu_highlights": "Menu highlights",
  "blocks.dietary_info": "Dietary info",
  "blocks.seating_info": "Seating info",
  "blocks.schedule_info": "Schedule",
  "blocks.access_info": "Access info",
  "blocks.why_play": "Why play here",
  "blocks.course_details": "Course details",
  "blocks.facilities": "Facilities",
  "blocks.why_visit": "Why visit",
  "blocks.what_to_expect": "What to expect",
  "blocks.best_time": "Best time",
  "blocks.how_to_combine": "How to combine",
  "blocks.opening_hours": "Opening hours",
  "blocks.practical_tips": "Practical tips",
  "blocks.why_eat": "Why eat here",
  "blocks.cuisine": "Cuisine",
  "blocks.price_range": "Price range",
  "blocks.why_stay": "Why stay",
  "blocks.nearby": "Nearby",
  "blocks.why_book": "Why book",
  "blocks.treatments": "Treatments",
  "blocks.overview": "Overview",
  "blocks.stops": "Stops",
  "blocks.itinerary": "Itinerary",
  "blocks.includes_excludes": "What's included",
};

function prettyTitle(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

export function blockTitle(titleKey: string, fallbackKey: string): string {
  return TITLE_KEY_LABELS[titleKey] ?? prettyTitle(fallbackKey);
}

export function buildListSections(
  template: string | null | undefined,
  blocks: Record<string, unknown> | null | undefined,
  locale: Locale,
): ListSection[] {
  const def = template ? templateRegistry[template] : null;
  if (!def) return [];
  return def.blocks
    .filter((b) => (b.fieldType ?? "localizedStringList") === "localizedStringList")
    .map((b) => ({
      key: b.key,
      title: blockTitle(b.titleKey, b.key),
      icon: b.icon,
      tone: b.tone,
      items: readList(blocks ?? null, b.key, locale),
    }))
    .filter((s) => s.items.length > 0);
}

export function getItineraryStops(
  template: string | null | undefined,
  blocks: Record<string, unknown> | null | undefined,
): ItineraryStop[] {
  const def = template ? templateRegistry[template] : null;
  if (!def) return [];
  const meta = def.blocks.find((b) => (b.fieldType ?? "localizedStringList") === "itinerary");
  if (!meta) return [];
  return readItinerary(blocks ?? null, meta.key);
}
